import type { MoneyFlow } from '@pfo/analytics';
import useSWR from 'swr';

import {
  fetchHouseholdMoneyFlow,
  fetchMoneyFlow,
  type HouseholdMoneyFlowQuery,
  type MoneyFlowQuery,
} from '../data/dashboardRepository.js';

const KEY_PREFIX = 'money-flow:';

export type MoneyFlowState =
  | { status: 'loading' }
  | { status: 'error'; message: string; retry: () => void }
  | { status: 'ready'; flow: MoneyFlow };

/**
 * The dashboard's figures for one subject and one month.
 *
 * The period travels inside the cache key, so stepping back to a month already visited is
 * instant and stepping forward does not discard what was read.
 */
export function useMoneyFlow(query: MoneyFlowQuery | null): MoneyFlowState {
  const { data, error, isLoading, mutate } = useSWR<MoneyFlow, Error>(
    query ? `${KEY_PREFIX}${JSON.stringify(query)}` : null,
    (key: string) => fetchMoneyFlow(JSON.parse(key.slice(KEY_PREFIX.length)) as MoneyFlowQuery),
    { revalidateOnFocus: false, shouldRetryOnError: false, keepPreviousData: true },
  );

  if (error) {
    return {
      status: 'error',
      message: error.message || 'Không tải được số liệu tháng này.',
      retry: () => {
        void mutate();
      },
    };
  }

  if (isLoading || data === undefined) return { status: 'loading' };

  return { status: 'ready', flow: data };
}

/**
 * The same month read as a family (step 1.8, spec 22.3).
 *
 * A hook of its own rather than a flag: the screen switching between "Me" and "Gia đình"
 * passes null to whichever it is not showing, so the figures on screen can never be a mix of
 * the two. The cache key keeps the shared `money-flow:` prefix, so a posted entry clears the
 * personal and the family answer together.
 */
export function useHouseholdMoneyFlow(query: HouseholdMoneyFlowQuery | null): MoneyFlowState {
  const { data, error, isLoading, mutate } = useSWR<MoneyFlow, Error>(
    query ? `${KEY_PREFIX}household:${JSON.stringify(query)}` : null,
    (key: string) =>
      fetchHouseholdMoneyFlow(
        JSON.parse(key.slice(`${KEY_PREFIX}household:`.length)) as HouseholdMoneyFlowQuery,
      ),
    { revalidateOnFocus: false, shouldRetryOnError: false, keepPreviousData: true },
  );

  if (error) {
    return {
      status: 'error',
      message: error.message || 'Không tải được số liệu của gia đình.',
      retry: () => {
        void mutate();
      },
    };
  }

  if (isLoading || data === undefined) return { status: 'loading' };

  return { status: 'ready', flow: data };
}
